import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { ACCOUNT_REALM } from '@core/config/account-realm.token';
import { SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE } from '@core/http/http-context-tokens';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { AuthCredentials } from '../../../domain/models/auth-credentials.model';
import {
  AuthenticatedUserDto, ClinicOwnerRegistrationTokenInfoDto, CompleteClinicOwnerRegistrationRequestDto,
  ForgotPasswordRequestDto, LoginRequestDto, RefreshTokenRequestDto, ResetPasswordRequestDto,
  ResetPasswordTokenInfoDto, TokenResponseDto,
} from '../../dtos/auth.dto';

@Injectable()
export class AuthRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);
  private readonly accountRealm = inject(ACCOUNT_REALM);

  // Every credential-class way a login can fail is already rendered inline by the login form, and
  // the global toast only knows the status: it would stack a generic "no tienes permiso" on top of
  // the form's own precise message for the 403s (deactivated account, suspended clinic, ban). That
  // carve-out only holds for statuses the form can actually render, though -- a genuine
  // service-unavailable-class status (see `SERVICE_UNAVAILABLE_STATUSES`) has no inline message
  // (`auth.store.ts`'s `login()` deliberately skips it), so the toast is left free to fire for it.
  public login(credentials: AuthCredentials): Observable<TokenResponseDto> {
    const request: LoginRequestDto = { Username: credentials.username, Password: credentials.password, AccountRealm: this.accountRealm };
    return this.http.post<TokenResponseDto>(`${this.apiUrl}${API_ENDPOINTS.auth.login}`, request, {
      context: new HttpContext().set(SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE, true),
    });
  }

  public refresh(refreshToken: string): Observable<TokenResponseDto> {
    const request: RefreshTokenRequestDto = { RefreshToken: refreshToken };
    return this.http.post<TokenResponseDto>(`${this.apiUrl}${API_ENDPOINTS.auth.refresh}`, request);
  }

  public logout(refreshToken: string): Observable<void> {
    const request: RefreshTokenRequestDto = { RefreshToken: refreshToken };
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.auth.logout}`, request);
  }

  public getProfile(): Observable<AuthenticatedUserDto> {
    return this.http.get<AuthenticatedUserDto>(`${this.apiUrl}${API_ENDPOINTS.auth.me}`);
  }

  public updateAvatar(file: File): Observable<AuthenticatedUserDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put<AuthenticatedUserDto>(`${this.apiUrl}${API_ENDPOINTS.auth.meAvatar}`, formData);
  }

  public deleteAvatar(): Observable<AuthenticatedUserDto> {
    return this.http.delete<AuthenticatedUserDto>(`${this.apiUrl}${API_ENDPOINTS.auth.meAvatar}`);
  }

  // No body: the server matches the caller to their own record in the clinic's provider staff list.
  // Answers 404 when there is no photo to copy, which is an ordinary outcome rather than a failure.
  public useProviderAvatar(): Observable<AuthenticatedUserDto> {
    return this.http.post<AuthenticatedUserDto>(`${this.apiUrl}${API_ENDPOINTS.auth.meAvatarFromProvider}`, null);
  }

  public forgotPassword(email: string): Observable<void> {
    const request: ForgotPasswordRequestDto = { Email: email, AccountRealm: this.accountRealm };
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.auth.forgotPassword}`, request);
  }

  public resetPassword(token: string, password: string, passwordConfirmation: string): Observable<void> {
    const request: ResetPasswordRequestDto = { Token: token, Password: password, PasswordConfirmation: passwordConfirmation };
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.auth.resetPassword}`, request);
  }

  public getResetPasswordTokenInfo(token: string): Observable<ResetPasswordTokenInfoDto> {
    return this.http.get<ResetPasswordTokenInfoDto>(`${this.apiUrl}${API_ENDPOINTS.auth.resetPasswordTokenInfo}`, {
      params: { token },
    });
  }

  public getInvitationCodeInfo(code: string): Observable<ResetPasswordTokenInfoDto> {
    return this.http.get<ResetPasswordTokenInfoDto>(`${this.apiUrl}${API_ENDPOINTS.auth.resetPasswordTokenInfo}`, {
      params: { code },
    });
  }

  public redeemInvitationCode(
    code: string, password: string, passwordConfirmation: string,
    acceptedLegalTermsVersion: number, acceptsMarketing: boolean,
    adultDeclarationConfirmed?: boolean, adultDeclarationVersion?: string,
  ): Observable<void> {
    const request: ResetPasswordRequestDto = {
      Code: code,
      Password: password,
      PasswordConfirmation: passwordConfirmation,
      AcceptedLegalTermsVersion: acceptedLegalTermsVersion,
      AcceptsMarketing: acceptsMarketing,
      AdultDeclarationConfirmed: adultDeclarationConfirmed,
      AdultDeclarationVersion: adultDeclarationVersion,
    };
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.auth.resetPassword}`, request);
  }

  public getClinicOwnerRegistrationTokenInfo(token: string): Observable<ClinicOwnerRegistrationTokenInfoDto> {
    return this.http.get<ClinicOwnerRegistrationTokenInfoDto>(
      `${this.apiUrl}${API_ENDPOINTS.auth.completeClinicOwnerRegistrationTokenInfo}`,
      { params: { token } },
    );
  }

  public completeClinicOwnerRegistration(
    token: string, password: string, passwordConfirmation: string, firstName: string, lastName: string,
  ): Observable<void> {
    const request: CompleteClinicOwnerRegistrationRequestDto = {
      AccountRealm: this.accountRealm,
      Token: token,
      Password: password,
      PasswordConfirmation: passwordConfirmation,
      FirstName: firstName,
      LastName: lastName,
    };
    return this.http.post<void>(`${this.apiUrl}${API_ENDPOINTS.auth.completeClinicOwnerRegistration}`, request);
  }
}
